/*
    Examinee test page
*/

import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useHistory, useLocation, useParams } from 'react-router-dom';

// Remember the questionnaire session an invite link started, so reopening the
// link resumes that session and its record instead of starting an empty one
// (PIN-221). localStorage, not sessionStorage: respondents reopen the emailed
// link in a new tab. Storage can be unavailable (Private Browsing); the link
// then simply starts a new session, as it always did.
const readRememberedSession = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn('Could not read the remembered questionnaire session:', error);
    return null;
  }
};

// The key rides alongside: the server takes writes to the session only with it
// (PIN-324). A session remembered before keys existed has none, and needs none.
const rememberSession = (key, sessionID, raterKey) => {
  try {
    localStorage.setItem(key, sessionID);
    if (raterKey) localStorage.setItem(`${key}:raterKey`, raterKey);
    else localStorage.removeItem(`${key}:raterKey`);
  } catch (error) {
    console.warn('Could not remember the questionnaire session:', error);
  }
};

// The questionnaire page reads the rater key from its URL, so a reload keeps it.
const questionnairePath = (sessionID, testID, raterKey) =>
  `/examinee/questionnaire/${sessionID}/${testID}` +
  (raterKey ? `?key=${encodeURIComponent(raterKey)}` : '');

const StartTestPage = () => {
  const { examineeID, examinerID, recordLabel, testID } = useParams();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const mode = searchParams.get('mode');
  const storageKey = `wici-questionnaire-session:${location.pathname}`;
  const [error, setError] = useState(null);

  // `isCancelled` reports whether the page unmounted while a request was in
  // flight; nothing navigates or updates state after that.
  const generateSession = useCallback(
    async (isCancelled = () => false) => {
      if (!examineeID) return;

      const config = {
        headers: {
          'Content-Type': 'application/json'
        }
      };

      // Resume only a session that is still administering its questionnaire. A
      // session the server refuses (ended or expired) is replaced by a new one;
      // an unreachable server is not evidence the session ended, so keep it and
      // ask the respondent to retry rather than orphan their answers.
      const remembered = readRememberedSession(storageKey);
      if (remembered) {
        const status = await axios
          .post(
            process.env.REACT_APP_API_URL + '/session/questionnaireItems',
            { sessionID: remembered },
            config
          )
          .then(
            () => 'active',
            (probeError) => (probeError.response ? 'ended' : 'unreachable')
          );
        if (isCancelled()) return;
        if (status === 'active') {
          history.push(
            questionnairePath(remembered, testID, readRememberedSession(`${storageKey}:raterKey`))
          );
          return;
        }
        if (status === 'unreachable') {
          setError('Could not connect. Please check your connection and reopen the link.');
          return;
        }
      }

      const data = {
        examineeID,
        examinerID,
        recordLabel,
        testID
      };

      const response = await axios.post(
        process.env.REACT_APP_API_URL + '/session/generate',
        data,
        config
      );
      if (!response)
        throw new Error(
          `Could not start the test: ${examineeID} ${examinerID} ${recordLabel} ${testID}`
        );
      if (isCancelled()) return;

      const { sessionID, raterKey } = response.data;
      const effectiveMode = response.data.mode || mode;
      if (effectiveMode === 'questionnaire') {
        rememberSession(storageKey, sessionID, raterKey);
        history.push(questionnairePath(sessionID, testID, raterKey));
      } else {
        history.push(`/examinee/test/${sessionID}`);
      }
    },
    [history, examineeID, examinerID, mode, recordLabel, storageKey, testID]
  );

  useEffect(() => {
    let cancelled = false;
    generateSession(() => cancelled).catch((startError) => {
      console.error('Could not start the test:', startError);
      if (!cancelled) setError('The test could not be started. Please reopen the link.');
    });
    return () => {
      cancelled = true;
    };
  }, [generateSession]);

  if (!examineeID) return 'An examinee is required to start a test.';
  if (error) return error;

  return 'One moment please...';
};

export default StartTestPage;
